type PropertyName = 'background-color' | 'color' | 'font-size' | 'font-family'

/**
 * Remove a CSS property from selected element(s)
 * Handles both full selection (remove from entire element) and partial selection (remove from portion)
 * Similar to applySelectedFormatToElement but for removal
 */
export const removeSelectedFormatFromElement = (
  editor: Editor,
  querySelector: string,
  propertyName: PropertyName,
): void => {
  const earlyReturn = !editor || !querySelector || !propertyName
  if (earlyReturn) return

  const selectedNode = editor?.selection?.getNode?.()
  const targetElement = selectedNode?.closest?.(querySelector)

  if (!targetElement) return

  const selectionRange = editor?.selection?.getRng?.()
  if (!selectionRange) return

  const isCollapsed = selectionRange.collapsed

  const noSelectionLetDefaultBehaviorHandleIt = isCollapsed
  if (noSelectionLetDefaultBehaviorHandleIt) return

  const selectionStartNode = selectionRange.startContainer
  const selectionEndNode = selectionRange.endContainer

  const startIsInTarget =
    targetElement.contains(selectionStartNode) ||
    selectionStartNode === targetElement
  const endIsInTarget =
    targetElement.contains(selectionEndNode) ||
    selectionEndNode === targetElement

  const targetText = targetElement.textContent || ''
  const selectedText = editor?.selection?.getContent?.({ format: 'text' }) || ''

  const isFullSelection =
    selectedText.trim() === targetText.trim() ||
    selectedText.trim().length >= targetText.trim().length

  const fullSelectionRemovePropertyFromEntireElement =
    isFullSelection && startIsInTarget && endIsInTarget
  const partialSelectionRemovePropertyFromSelectedPortion =
    startIsInTarget || endIsInTarget

  if (fullSelectionRemovePropertyFromEntireElement) {
    removeStyleRecursive(editor, targetElement, propertyName)
    editor?.nodeChanged?.()
    editor?.fire?.('change')
  } else if (partialSelectionRemovePropertyFromSelectedPortion) {
    const selectedContent = editor?.selection?.getContent?.()
    if (!selectedContent) return

    const allElements: Element[] = []
    const findElementsInRange = (node: Node): void => {
      const nodeIsElement = node.nodeType === Node.ELEMENT_NODE
      const nodeIsTargetElement = node === targetElement
      const nodeIsInRange = selectionRange.intersectsNode(node)
      const nodeIsInTarget = targetElement.contains(node)
      const nodeIsInRangeAndInTargetAndNotTargetElement =
        nodeIsInTarget && nodeIsInRange && !nodeIsTargetElement
      if (nodeIsElement) {
        if (nodeIsInRangeAndInTargetAndNotTargetElement) {
          allElements.push(node as Element)
        }
        Array.from(node.childNodes).forEach(findElementsInRange)
      }
    }

    findElementsInRange(targetElement)

    const leafElements = allElements.filter((element) => {
      const hasSelectedChildren = allElements.some(
        (other) => element !== other && element.contains(other),
      )
      return !hasSelectedChildren
    })

    leafElements.forEach((element) => {
      removeStyleRecursive(editor, element, propertyName)
    })

    editor?.nodeChanged?.()
    editor?.fire?.('change')
  }
}

const removeStyleRecursive = (
  editor: Editor,
  node: Node | HTMLElement,
  propertyName: PropertyName,
): void => {
  const badNode = !node || node.nodeType !== Node.ELEMENT_NODE
  if (badNode) return

  if (!(node instanceof HTMLElement)) return

  if (!editor?.dom?.setStyle) return

  editor.dom.setStyle(node, propertyName, null)

  Array.from(node.children).forEach((child) =>
    removeStyleRecursive(editor, child, propertyName),
  )
}
